import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as uiSlice from '../../../../data/ui.slice';
import * as clientSlice from '../../../../data/client.slice';
import { ClientLocation } from '../../../../data/models/client-location.model';
import clientUtils from '../../../../services/clientUtils';
import ClientLocationForm from './ClientLocationForm';

const PREFIX = 'ClientLocationDialog';

const classes = {
  content: `${PREFIX}-content`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: 500,
  },
}));

interface ClientLocationDialogProps {
  clientBC: string;
  location?: ClientLocation;
  open: boolean;
  onClose: () => void;
}

export default function ClientLocationDialog({
  clientBC,
  location,
  open,
  onClose,
}: ClientLocationDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const isSavingNamedLocation = useAppSelector(
    clientSlice.selectSavingNamedLocation,
  );
  const isSavingClientLocation = useAppSelector(
    clientSlice.selectSavingClientLocation,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const namedLocations = useAppSelector(clientSlice.selectNamedLocations);

  const namedLocation = useMemo(() => {
    if (location?.namedLocationUUID) {
      return namedLocations.find(
        (nl) => nl.namedLocationUUID === location.namedLocationUUID,
      );
    }
    return undefined;
  }, [location, namedLocations]);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getClientLocationDefaults(
      location,
      namedLocation,
    ),
  });

  const addressStruct = formHook.watch('addressStruct');
  const address = useMemo(
    () => (addressStruct ? JSON.parse(addressStruct) : undefined),
    [addressStruct],
  );
  const isAddressFullyPopulated = useMemo(
    () => clientUtils.isAddressFullyPopulated(address),
    [address],
  );

  useEffect(() => {
    formHook.reset(
      clientUtils.getClientLocationDefaults(location, namedLocation),
    );
  }, [location, namedLocation]);

  useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  const handleClose = () => {
    formHook.reset();

    dispatch(clientSlice.reset());
    onClose();
  };

  const handleSave = () => {
    const data = formHook.getValues();
    if (namedLocation) {
      const payload = clientUtils.getEditableNamedAddressFromFormData(
        namedLocation,
        data,
      );
      dispatch(clientSlice.updateClientLocationAsync(payload));
    } else {
      const creatableNamedLocation =
        clientUtils.getCreatableNamedAddressFromFormData(data);
      const payload = {
        clientBC,
        description: data.description,
        forUseAs: data.forUseAs,
      };
      dispatch(
        clientSlice.createClientLocationAsync(creatableNamedLocation, payload),
      );
    }
  };

  const disableSaveButton =
    isSavingNamedLocation ||
    isSavingClientLocation ||
    !formHook.formState.isValid ||
    Object.keys(formHook.formState.errors).length > 0 ||
    !isAddressFullyPopulated;

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {namedLocation
          ? t('namedLocations.editNamedLocation')
          : t('namedLocations.addNamedLocation')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <ClientLocationForm formHook={formHook} isEdit={!!location} />
        {address && !isAddressFullyPopulated && (
          <Alert severity="error">
            {t('client.addressIsNotDetailedError')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          disabled={isSavingClientLocation || isSavingNamedLocation}
          onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={disableSaveButton}
          onClick={handleSave}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
